import React, { useMemo } from "react";
import PropTypes from "prop-types";
import SearchBar from "shared/SearchBar";
import { Button, ButtonPurpose, ButtonSize } from "shared/Button";
import AssignedAudioRow from "../AssignedAudioRow";
import PlusSquareIcon from "shared/assets/plus-square.svg";
import styles from "./SpeakerAudiosSection.module.css";

const noop = () => {};

const parseDate = (dateValue) => {
  if (!dateValue) {
    return 0;
  }

  const directTimestamp = Date.parse(dateValue);
  if (!Number.isNaN(directTimestamp)) {
    return directTimestamp;
  }

  const slashDateMatch = String(dateValue)
    .trim()
    .match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);

  if (!slashDateMatch) {
    return 0;
  }

  const [, month, day, year] = slashDateMatch;
  return new Date(Number(year), Number(month) - 1, Number(day)).getTime();
};

export function SpeakerAudiosSection({
  audios,
  searchValue,
  onSearch,
  onAdd,
  onRemove,
  onEdit,
}) {
  const sortedAudios = useMemo(
    () => [...audios].sort((a, b) => parseDate(b.date) - parseDate(a.date)),
    [audios],
  );

  const visibleAudios = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLowerCase();

    if (!normalizedSearch) {
      return sortedAudios;
    }

    return sortedAudios.filter((audio) => {
      const searchableText = [audio.title, audio.speaker, audio.episodePart]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchableText.includes(normalizedSearch);
    });
  }, [searchValue, sortedAudios]);

  const hasAudios = audios.length > 0;
  const hasNoMatches = hasAudios && visibleAudios.length === 0;

  return (
    <section className={styles.card}>
      <div className={styles.headerRow}>
        <h3 className={styles.title}>Speaker Media</h3>
        <div className={styles.controlsRow}>
          <div className={styles.searchWrap}>
            <SearchBar
              placeholderText="Search Media"
              value={searchValue}
              onInput={onSearch}
              searchDelay={0}
              minChar={0}
            />
          </div>
          <Button
            purpose={ButtonPurpose.primary}
            size={ButtonSize.small}
            className={styles.addButton}
            onClick={onAdd}
            icon={
              <span className={styles.addIconWrap}>
                <img src={PlusSquareIcon} alt="" className={styles.addIcon} />
              </span>
            }
          >
            Add
          </Button>
        </div>
      </div>

      <div className={styles.divider} />

      {!hasAudios ? (
        <p className={styles.emptyState}>No Media Added</p>
      ) : hasNoMatches ? (
        <p className={styles.emptyState}>No Matching Media</p>
      ) : (
        <ul className={styles.list} aria-label="Speaker media list">
          {visibleAudios.map((audio) => (
            <li
              key={audio.assignmentId || audio.id}
              className={styles.listItem}
            >
              <AssignedAudioRow
                thumbnail={audio.thumbnail}
                title={audio.title}
                date={audio.date}
                speaker={audio.speaker}
                episodePart={audio.episodePart}
                onRemove={() => onRemove(audio.assignmentId || audio.id)}
                onEdit={() => onEdit(audio.id)}
                removeAriaLabel="Remove audio"
                draggable={false}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

SpeakerAudiosSection.propTypes = {
  audios: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      assignmentId: PropTypes.string,
      thumbnail: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      date: PropTypes.string.isRequired,
      speaker: PropTypes.string.isRequired,
      episodePart: PropTypes.string.isRequired,
    }),
  ),
  searchValue: PropTypes.string,
  onSearch: PropTypes.func,
  onAdd: PropTypes.func,
  onRemove: PropTypes.func,
  onEdit: PropTypes.func,
};

SpeakerAudiosSection.defaultProps = {
  audios: [],
  searchValue: "",
  onSearch: noop,
  onAdd: noop,
  onRemove: noop,
  onEdit: noop,
};

export default SpeakerAudiosSection;
